import { Anchor } from 'two.js/src/anchor';
import Two from 'two.js';
import { getCoordinatesInCircle } from 'rf-ui/GameRenderer/rendererHelper';

type BalloonParams = {
  color: string;
  scale: number;
};

export const createBalloon = (two: Two, inputParams: Partial<BalloonParams> = {}) => {
  const params: BalloonParams = {
    color: 'red',
    scale: 30,
    ...inputParams,
  };
  const scale = 30;
  const balloon = two.makeCurve([
    new Anchor(0, -scale),
    new Anchor(-scale, 0),
    new Anchor(0, scale * 1.8),
    new Anchor(scale, 0),
  ]);
  const gradient = new Two.RadialGradient(
    -scale * 0.3, -scale * 0.3,
    scale * 0.4,
    [
      new Two.Stop(0, 'rgb(255, 255, 255)', 1),
      new Two.Stop(1, params.color, 1),
    ]
  );
  gradient.units = 'userSpaceOnUse';
  balloon.fill = gradient;
  balloon.stroke = 'white';
  balloon.linewidth = 2;

  const tie = two.makePath([
    new Anchor(-scale * 0.1, scale * 1.7),
    new Anchor(-scale * 0.2, scale * 2),
    new Anchor(0, scale * 1.9),
    new Anchor(scale * 0.2, scale * 2),
    new Anchor(scale * 0.1, scale * 1.7),
  ])
    .noStroke();

  const rope = two.makeCurve([
    new Anchor(-scale * 0.35, scale * 1.7),
    new Anchor(scale * 0.2, scale * 4),
    new Anchor(0, scale * 6)
  ]);
  rope.fill = 'none';
  rope.linewidth = 3;
  rope.stroke = 'white';
  rope.closed = false;

  return two.makeGroup(tie, rope, balloon);
};

type CrownParams = {
  text: string;
  color: string;
};

export const createCrown = (two: Two, inputParams: Partial<CrownParams> = {}) => {
  const params: CrownParams = {
    text: '1',
    color: 'red',
    ...inputParams,
  };
  const scale = 10;
  const crown = two.makePath([
    new Two.Anchor(-scale * 6, scale * 2),
    new Two.Anchor(-scale * 6, -scale * 3),
    new Two.Anchor(-scale * 5, -scale * 1),
    new Two.Anchor(-scale * 4, -scale * 2),
    new Two.Anchor(-scale * 3, -scale * 1),
    new Two.Anchor(-scale * 2, -scale * 2),
    new Two.Anchor(-scale * 1, -scale * 1),
    new Two.Anchor(0, -scale * 2),
    new Two.Anchor(scale * 1, -scale * 1),
    new Two.Anchor(scale * 2, -scale * 2),
    new Two.Anchor(scale * 3, -scale * 1),
    new Two.Anchor(scale * 4, -scale * 2),
    new Two.Anchor(scale * 5, -scale * 1),
    new Two.Anchor(scale * 6, -scale * 3),
    new Two.Anchor(scale * 6, scale * 2),
  ]);
  crown.fill = params.color;
  crown.stroke = 'white';
  crown.linewidth = 2;

  const circle = two.makeCircle(0, scale * 0.5, scale * 1.3);
  circle.fill = 'black';
  circle.noStroke();

  const text = two.makeText(params.text, 0, scale * 0.5);
  text.alignment = 'center';
  text.baseline = 'middle';
  text.size = scale * 2;
  text.noStroke();
  text.fill = 'white';

  return two.makeGroup(crown, circle, text);
};

type ConfettiParams = {
  scale: number;
  color: string;
};

export const createConfetti = (two: Two, inputParams: Partial<ConfettiParams> = {}) => {
  const params: ConfettiParams = {
    scale: 1,
    color: 'red',
    ...inputParams,
  };

  const size = 10;
  const randomFact = 6;
  const shape = two.makePath([
    new Two.Anchor(-size - Math.random() * randomFact, -size - Math.random() * randomFact),
    new Two.Anchor(-size - Math.random() * randomFact, size + Math.random() * randomFact),
    new Two.Anchor(size + Math.random() * randomFact, size + Math.random() * randomFact),
    new Two.Anchor(size + Math.random() * randomFact, -size - Math.random() * randomFact),
  ]);
  shape.noStroke();
  shape.fill = params.color;
  return shape;
};

type DiscoballParams = {
  scale: number;
};

type Point2D = { x: number; y: number };

export const createDiscoball = (two: Two, inputParams: Partial<DiscoballParams> = {}) => {
  const params: DiscoballParams = {
    scale: 1,
    ...inputParams,
  };
  const ball = two.makeCircle(0, 0, params.scale * 80);
  ball.stroke = 'white';
  ball.fill = '#221b1b';
  const colors: string[] = [
    'rgb(78,187,246)',
    'rgb(239,37,37)',
    'rgb(255,218,65)',
    'rgb(78,246,81)',
    'rgb(78,187,246)',
  ];
  const lights = colors
    .map((color, index): { color: string; coords: Point2D } => ({
      color,
      coords: getCoordinatesInCircle(50, Math.PI * 2 * (index / colors.length)),
    }))
    .map(light => {
      const circle = two.makeCircle(light.coords.x, light.coords.y, params.scale * 15);
      circle.noStroke();
      circle.fill = light.color;
      return circle;
    });

  /*
  const verLine1 = two.makeCurve([
    new Anchor(-80, 0),
    new Anchor(-60, 20),
    new Anchor(60, 20),
    new Anchor(80, 0)]);
  verLine1.stroke= 'white';
  verLine1.closed = false;
  verLine1.noFill();
  group.add(verLine1);
   */

  return two.makeGroup([ball, ...lights]);
};
